// Frontend/src/api/residents.ts
import api from "./client";

export type ResidentRole = "owner" | "tenant";
export type ResidentLanguage = "en" | "es";

export type Resident = {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  role: ResidentRole;
  role_display: string;
  unit: string; // unit ID
  unit_details: {
    id: string;
    unit_code: string;
    building_id: string | null;
    building_name: string | null;
    building_code: string | null;
    floor: string | null;
    number: string | null;
    monthly_fee: string;
    current_balance: string;
    status: string;
    status_display: string;
    resident_count: number;
  };
  language: ResidentLanguage;
  language_display: string;
  is_primary: boolean;
  cedula: string | null;
  id_document_image: string | null;
  user: string | null;
  created_at: string;
  updated_at: string;
};

export type ResidentList = {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  role: ResidentRole;
  role_display: string;
  unit_code: string;
  unit_building: string | null;
  language: ResidentLanguage;
  is_primary: boolean;
  has_completed_setup: boolean;
  cedula: string | null;
  id_document_image: string | null;
};

export type CreateResidentData = {
  full_name: string;
  email: string;
  phone?: string;
  role?: ResidentRole;
  unit: string; // unit ID
  language?: ResidentLanguage;
  is_primary?: boolean;
  cedula?: string;
  id_document_image?: File | null;
};

export type UpdateResidentData = Partial<CreateResidentData>;

export type ResidentCSVImportResult = {
  success: boolean;
  total_processed: number;
  success_count: number;
  error_count: number;
  errors: Array<{
    row: number;
    field: string;
    message: string;
    data: Record<string, any>;
  }>;
  created_resident_ids: string[];
};

/**
 * List all residents for the current building
 */
export async function listResidents(): Promise<ResidentList[]> {
  const res = await api.get<ResidentList[]>("/building/residents/");
  return res.data;
}

/**
 * Get a single resident by ID
 */
export async function getResident(id: string): Promise<Resident> {
  const res = await api.get<Resident>(`/building/residents/${id}/`);
  return res.data;
}

/**
 * Build FormData from resident data (used when id_document_image is a File)
 */
function buildResidentFormData(data: CreateResidentData | UpdateResidentData): FormData {
  const fd = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (value instanceof File) {
      fd.append(key, value);
    } else {
      fd.append(key, String(value));
    }
  });
  return fd;
}

/**
 * Create a new resident
 */
export async function createResident(data: CreateResidentData): Promise<Resident> {
  if (data.id_document_image instanceof File) {
    const res = await api.post<Resident>("/building/residents/", buildResidentFormData(data));
    return res.data;
  }
  const res = await api.post<Resident>("/building/residents/", data);
  return res.data;
}

/**
 * Update an existing resident
 */
export async function updateResident(id: string, data: UpdateResidentData): Promise<Resident> {
  if (data.id_document_image instanceof File) {
    const res = await api.patch<Resident>(`/building/residents/${id}/`, buildResidentFormData(data));
    return res.data;
  }
  const res = await api.patch<Resident>(`/building/residents/${id}/`, data);
  return res.data;
}

/**
 * Delete a resident
 */
export async function deleteResident(id: string): Promise<void> {
  await api.delete(`/building/residents/${id}/`);
}

/**
 * Download CSV template for resident import
 */
export async function downloadResidentCSVTemplate(): Promise<string> {
  const res = await api.get<string>("/building/residents/csv-template/");
  return res.data;
}

/**
 * Import residents from CSV file
 */
export async function importResidentsCSV(file: File): Promise<ResidentCSVImportResult> {
  const formData = new FormData();
  formData.append("file", file);

  const res = await api.post<ResidentCSVImportResult>("/building/residents/import-csv/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return res.data;
}
